import React, { useState } from 'react';
import {
  PLAN_METRICS,
  PLAN_SCHEDULE,
  MEMBERSHIP_PLANS,
} from '../data/appData';
import { openWhatsAppMessage } from '../utils/contact';

// Helper to get icon based on session name
const getSessionIcon = (name) => {
  if (name.includes('Boxeo') || name.includes('Box')) return '🥊';
  if (name.includes('Baile') || name.includes('Dance')) return '💃';
  if (name.includes('Funcional')) return '🔥';
  return '💪';
};

// Fake dates for the demo schedule
const getDayNumber = (dayName) => {
  const map = { 'Lunes': '21', 'Martes': '22', 'Miércoles': '23', 'Jueves': '24', 'Viernes': '25' };
  return map[dayName] || '01';
};

function MetricPill({ label, value, tone }) {
  return (
    <div className={tone ? `metric-pill metric-pill-${tone}` : 'metric-pill'}>
      <span className="metric-label">{label}</span>
      <span className="metric-value">{value}</span>
    </div>
  );
}

// Mock Events Map (Date -> Sessions)
const EVENTS_MAP = {
  '21': [{ id: 'm1', session: 'Entrenamiento Funcional', time: '19:00', coach: 'Staff' }, { id: 'm2', session: 'Baile Entretenido', time: '20:15', coach: 'Staff' }],
  '22': [{ id: 't1', session: 'Boxeo', time: '19:00', coach: 'Staff' }],
  '23': [{ id: 'w1', session: 'Entrenamiento Funcional', time: '19:00', coach: 'Staff' }, { id: 'w2', session: 'Baile Entretenido', time: '20:15', coach: 'Staff' }],
  '24': [{ id: 'th1', session: 'Boxeo', time: '19:00', coach: 'Staff' }],
  '28': [{ id: 'f1', session: 'Baile Entretenido', time: '20:15', coach: 'Staff' }],
  '29': [{ id: 'f2', session: 'Boxeo', time: '19:00', coach: 'Staff' }],
  '30': [{ id: 'f3', session: 'Funcional', time: '19:00', coach: 'Staff' }],
};

function PlanView() {
  const activePlanId = 'three-week';
  const activePlan = MEMBERSHIP_PLANS.find((plan) => plan.id === activePlanId);
  const userAvatar = "https://images.unsplash.com/photo-1556817411-31ae72fa3ea0?auto=format&fit=crop&w=400&q=80";

  const [selectedSession, setSelectedSession] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDay, setCalendarDay] = useState('21'); // Default selected day

  // Generate 30 days for current month view
  const daysArray = Array.from({ length: 30 }, (_, i) => String(i + 1));
  const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  const handleOpenSession = (session, dayOverride) => {
    setSelectedSession({ ...session, day: dayOverride || session.day });
  };

  const handleCloseSession = () => {
    setSelectedSession(null);
  };

  const handleBookSession = () => {
    if (!selectedSession) return;
    // Handle date formatting if day is just a number
    const dayDisplay = selectedSession.day.length <= 2 ? `Día ${selectedSession.day}` : selectedSession.day;
    const msg = `Hola! Quiero reservar cupo para *${selectedSession.session}* el ${dayDisplay} a las ${selectedSession.time}.`;
    openWhatsAppMessage(msg);
    handleCloseSession();
  };

  return (
    <div className="screen">
      {/* Session Detail Modal */}
      {selectedSession && (
        <div className="plan-detail-modal" style={{ zIndex: 2000 }}>
          <div className="plan-detail-card">
            <button type="button" className="plan-detail-close" onClick={handleCloseSession}>×</button>
            <header className="plan-detail-header">
              <span className="chip chip-accent">Clase Grupal</span>
              <h2 className="plan-detail-title">{selectedSession.session}</h2>
              <p className="plan-detail-price">{selectedSession.day.length <= 2 ? `Día ${selectedSession.day}` : selectedSession.day} · {selectedSession.time}</p>
            </header>

            <div className="plan-detail-body">
              <div style={{ marginBottom: '1.5rem' }}>
                <img
                  src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=500&q=80"
                  alt="Class context"
                  style={{ width: '100%', borderRadius: '12px', height: '150px', objectFit: 'cover', marginBottom: '1rem' }}
                />
                <h3 className="plan-detail-subtitle">Instructor</h3>
                <p style={{ color: 'white', marginBottom: '0.5rem' }}>{selectedSession.coach}</p>

                <h3 className="plan-detail-subtitle">Descripción</h3>
                <p style={{ color: '#ccc', fontSize: '0.9rem' }}>
                  Reserva tu cupo para asegurar tu asistencia. Recuerda traer tu toalla y agua.
                </p>
              </div>

              <div className="plan-detail-actions">
                <button type="button" className="pill-button pill-button-primary" onClick={handleBookSession}>
                  Reservar Cupo (WhatsApp)
                </button>
                <button type="button" className="pill-button pill-button-ghost" onClick={handleCloseSession}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Full Modal */}
      {showCalendar && (
        <div className="plan-detail-modal" style={{ zIndex: 1500 }}>
          <div className="plan-detail-card" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <button className="plan-detail-close" onClick={() => setShowCalendar(false)}>×</button>
            <h2 className="section-title">Calendario Noviembre</h2>

            {/* Grid */}
            <div className="calendar-grid">
              {weekDays.map(d => <div key={d} className="calendar-day-header">{d}</div>)}
              {daysArray.map(day => {
                const hasEvent = !!EVENTS_MAP[day];
                const isSelected = calendarDay === day;
                return (
                  <div
                    key={day}
                    className={`calendar-day ${hasEvent ? 'has-event' : ''} ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => setCalendarDay(day)}
                  >
                    {day}
                  </div>
                );
              })}
            </div>

            {/* Selected Day Agenda */}
            <div className="section-header">
              <h3 className="section-title-sm">Agenda Día {calendarDay}</h3>
            </div>

            <div className="schedule-list">
              {EVENTS_MAP[calendarDay] ? (
                EVENTS_MAP[calendarDay].map(evt => (
                  <div key={evt.id} className="schedule-row" onClick={() => handleOpenSession(evt, String(calendarDay))}>
                    <div className="schedule-info">
                      <h3 className="schedule-title">{evt.session}</h3>
                      <div className="schedule-meta">
                        <span>{getSessionIcon(evt.session)}</span>
                        <span>{evt.time}</span>
                        <span>· {evt.coach}</span>
                      </div>
                    </div>
                    <div className="schedule-action">›</div>
                  </div>
                ))
              ) : (
                <p style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '1rem' }}>No hay clases programadas.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Member Digital Credential */}
      <section className="member-card-container">
        <div className="member-card">
          <div className="card-header">
            <span className="card-brand">SPORTIVOS</span>
            <span className="card-status">ACTIVO</span>
          </div>
          <div className="card-body">
            <div className="member-info">
              <img src={userAvatar} alt="User" className="member-avatar" />
              <div className="member-details">
                <h3>Javier (Tú)</h3>
                <p>{activePlan?.name || 'Plan Fitness'}</p>
              </div>
            </div>
            <div className="qr-code">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=SPORTIVOS-MEMBER-001" alt="Access QR" />
            </div>
          </div>
        </div>
      </section>

      {/* Visual Stats Row */}
      <section style={{ padding: '0 1rem', marginBottom: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
        <div style={{ background: '#1e1e1e', padding: '0.8rem', borderRadius: '12px', flex: 1, textAlign: 'center' }}>
          <span style={{ display: 'block', fontSize: '1.5rem' }}>7/12</span>
          <span style={{ fontSize: '0.75rem', color: '#888' }}>Asistencias</span>
        </div>
        <div style={{ background: '#1e1e1e', padding: '0.8rem', borderRadius: '12px', flex: 1, textAlign: 'center' }}>
          <span style={{ display: 'block', fontSize: '1.5rem' }}>8</span>
          <span style={{ fontSize: '0.75rem', color: '#888' }}>Racha Días</span>
        </div>
        <div style={{ background: '#1e1e1e', padding: '0.8rem', borderRadius: '12px', flex: 1, textAlign: 'center' }}>
          <span style={{ display: 'block', fontSize: '1.5rem' }}>28</span>
          <span style={{ fontSize: '0.75rem', color: '#888' }}>Renueva Nov</span>
        </div>
      </section>

      {/* Schedule List (Preview) */}
      <section className="section">
        <header className="section-header">
          <h2 className="section-title">Tu Agenda</h2>
          <button type="button" className="link-button" onClick={() => setShowCalendar(true)}>
            Ver mes completo
          </button>
        </header>

        <div className="schedule-list">
          {PLAN_SCHEDULE.map((item) => (
            <div key={item.id} className="schedule-row" onClick={() => handleOpenSession(item)}>
              <div className="date-badge">
                <span className="date-day">{item.day.substring(0, 3)}</span>
                <span className="date-num">{getDayNumber(item.day)}</span>
              </div>
              <div className="schedule-info">
                <h3 className="schedule-title">{item.session}</h3>
                <div className="schedule-meta">
                  <span>{getSessionIcon(item.session)}</span>
                  <span>{item.time} hrs</span>
                  <span>· {item.coach}</span>
                </div>
              </div>
              <div className="schedule-action">
                ›
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default PlanView;
